import { ReleaseLock, acquireCancelableSemaphore } from "../../lib/utils/CancelableSemaphoreAcquire.js";
import { MediaResource } from "../player/MediaResource.js";
import CancelablePromise from "cancelable-promise";
import { BrowserLogger } from "@ciaopeople/cp-shatsu/Logging";
import Semaphore from "semaphore-promise";

import { HasResourceCleanupFeature, MediaGarbageCollector } from "./MediaGarbageCollector.js";


export interface GCOptions {
    maxResources: number,
    tickInSeconds: number
}
/**
 * Implements a garbage collector for media resources, managing their lifecycle and cleanup.
 * 
 * @template MediaPlayer - The type representing the media player.
 * @template RES - The type of media resource, extending `MediaResource`.
 * 
 * @implements {MediaGarbageCollector<MediaPlayer, RES>}
 * 
 * @remarks
 * - Maintains a queue of resources and periodically checks if the number of resources exceeds the configured maximum.
 * - Cleans up excess resources using the provided owner.
 * - Uses a semaphore to ensure thread-safe operations.
 * - Logging is performed for debugging and monitoring.
 * 
 * @param config - Configuration options for garbage collection, including tick interval and maximum resources.
 * @param win - The window object used for scheduling periodic checks.
 * @param owner - The owner responsible for cleaning up resources.
 * 
 * @method start - Starts the garbage collection process.
 * @method addResource - Adds a resource to the queue, ensuring thread safety.
 * @method addResourceAsync - Adds an asynchronously created resource to the queue.
 * @method lock - Acquires the semaphore lock for manual resource management.
 */
export class StandardGC<MediaPlayer, RES extends MediaResource<MediaPlayer, any, any>> implements MediaGarbageCollector<MediaPlayer, RES> {

    

    private resourceQueue: RES[];
    private gcStarted: boolean;
    
    private tickTime: number;
    private maxResources: number;
    private semaphore: Semaphore;
    private logger: BrowserLogger;

    constructor(private config: GCOptions, 
                private readonly win: Window,
                private readonly owner: HasResourceCleanupFeature<MediaPlayer, RES>) {

        this.gcStarted = false;
        this.resourceQueue = [];
        this.tickTime = config.tickInSeconds > 1?config.tickInSeconds*1000:5000;
        this.maxResources = config.maxResources > 4?config.maxResources:5;
        this.semaphore = new Semaphore(1);
        this.logger = new BrowserLogger("GC");
    }

    start(): void {
        if(!this.gcStarted) this.tick();
    }

    private tick() {
        this.gcStarted = true;
        this.logger.debug("Check Running");
        this.semaphore.acquire().then((release) => {
            
            let prom: Promise<void> = Promise.resolve();
            //this.logger.debug("Qeueue",this.resourceQueue);
            if(this.resourceQueue.length > this.maxResources) {
                
                const toDelete = this.resourceQueue.slice(0,this.resourceQueue.length - this.maxResources);
                //this.logger.log("toDelete",toDelete);
                this.resourceQueue = this.resourceQueue.slice(-this.maxResources);
                prom = toDelete.reduce((prevProm,el) => {
                    return prevProm.then((x) => this.owner.cleanResource(el))
                },Promise.resolve());

            }
            prom.then((x) => {
                release();
                this.win.setTimeout(() => this.tick(),this.tickTime);
            });
        });
        
    }



    private unsafeAdd(resource: RES): void {
        this.resourceQueue = this.resourceQueue.
            filter(x => x.info.id != resource.info.id);
            this.resourceQueue.push(resource);
        
    }

    addResource(resource: RES): Promise<RES> {
        return this.semaphore.acquire().then((release) => {
            this.unsafeAdd(resource);
            release();
            return resource;
        })
    }


    addResourceAsync(asyncResource: CancelablePromise<RES>): CancelablePromise<RES> {
        return acquireCancelableSemaphore(this.semaphore).then((release) => {
            return asyncResource.then(res => {
                this.unsafeAdd(res);
                release();
                return asyncResource;
            }).catch(e => {
                release();
                return asyncResource
            })
        });
    }

        
    lock(): Promise<ReleaseLock> {  
        return this.semaphore.acquire();
    }
    

}